import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Pencil,
  Trash2,
} from "lucide-react";
import type { SspLocationResponse } from "../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormTextarea,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { getPageWindow } from "../utils/pagination";

export interface LocationsTabProps {
  locationsLoading: boolean;
  locations: SspLocationResponse[];
  pagLocations: SspLocationResponse[];
  filtLocations: SspLocationResponse[];
  editingLocationId: string | null;
  setEditingLocationId: React.Dispatch<React.SetStateAction<string | null>>;
  editLocationForm: {
    title: string;
    addressLines: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  setEditLocationForm: React.Dispatch<
    React.SetStateAction<{
      title: string;
      addressLines: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    }>
  >;
  savingLocation: boolean;
  handleUpdateLocation: () => void;
  isPublishedSsp: boolean;
  requestDelete: (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => void;
  deletingLocationId: string | null;
  handleDeleteLocation: (locationDbId: string) => Promise<void>;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function LocationsTab({
  locationsLoading,
  locations,
  pagLocations,
  filtLocations,
  editingLocationId,
  setEditingLocationId,
  editLocationForm,
  setEditLocationForm,
  savingLocation,
  handleUpdateLocation,
  isPublishedSsp,
  requestDelete,
  deletingLocationId,
  handleDeleteLocation,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: LocationsTabProps) {
  return (
    <>
      {locationsLoading ? (
        <LoadingSpinner text="Loading locations" />
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {locations.length > 0 ? (
              <div className="divide-y divide-border">
                {pagLocations.map((loc) => (
                  <div
                    key={loc.id}
                    className="px-5 py-3.5 hover:bg-muted/40 transition-colors"
                  >
                    {editingLocationId === loc.id ? (
                      /* ── Inline edit form ── */
                      <div className="space-y-3">
                        <FormField label="Title" required>
                          <FormInput
                            value={editLocationForm.title}
                            onChange={(v) =>
                              setEditLocationForm((f) => ({
                                ...f,
                                title: v,
                              }))
                            }
                            placeholder="Location title"
                          />
                        </FormField>
                        <FormField label="Address Lines (one per line)">
                          <FormTextarea
                            value={editLocationForm.addressLines}
                            onChange={(v) =>
                              setEditLocationForm((f) => ({
                                ...f,
                                addressLines: v,
                              }))
                            }
                            placeholder="Street address"
                          />
                        </FormField>
                        <div className="grid grid-cols-4 gap-3">
                          <FormField label="City">
                            <FormInput
                              value={editLocationForm.city}
                              onChange={(v) =>
                                setEditLocationForm((f) => ({
                                  ...f,
                                  city: v,
                                }))
                              }
                              placeholder="City"
                            />
                          </FormField>
                          <FormField label="State">
                            <FormInput
                              value={editLocationForm.state}
                              onChange={(v) =>
                                setEditLocationForm((f) => ({
                                  ...f,
                                  state: v,
                                }))
                              }
                              placeholder="State"
                            />
                          </FormField>
                          <FormField label="Postal Code">
                            <FormInput
                              type="number"
                              value={editLocationForm.postalCode}
                              onChange={(v) =>
                                setEditLocationForm((f) => ({
                                  ...f,
                                  postalCode: v,
                                }))
                              }
                              placeholder="Zip"
                            />
                          </FormField>
                          <FormField label="Country">
                            <FormInput
                              value={editLocationForm.country}
                              onChange={(v) =>
                                setEditLocationForm((f) => ({
                                  ...f,
                                  country: v,
                                }))
                              }
                              placeholder="US"
                            />
                          </FormField>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => setEditingLocationId(null)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdateLocation}
                            disabled={
                              !editLocationForm.title || savingLocation
                            }
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                          >
                            {savingLocation ? "Saving" : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Read-only display ── */
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center size-10 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 flex-shrink-0 ring-1 ring-rose-100 dark:ring-rose-500/20">
                          <MapPin className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{loc.title}</p>
                          <div className="flex items-center gap-x-3 gap-y-1 mt-1 flex-wrap text-xs">
                            {loc.addressLines.length > 0 && (
                              <span className="text-muted-foreground">
                                {loc.addressLines.join(", ")}
                              </span>
                            )}
                            {(loc.city || loc.state || loc.postalCode) && (
                              <span className="inline-flex items-center gap-1 text-muted-foreground">
                                <span className="size-1 rounded-full bg-muted-foreground/50 hidden sm:inline-block" />
                                {[loc.city, loc.state, loc.postalCode]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                            {loc.country && (
                              <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 tracking-wide">
                                {loc.country}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                        >
                          <Tip label="Edit Location" side="bottom">
                            <button
                              onClick={() => {
                                setEditingLocationId(loc.id);
                                setEditLocationForm({
                                  title: loc.title,
                                  addressLines:
                                    loc.addressLines.join("\n"),
                                  city: loc.city || "",
                                  state: loc.state || "",
                                  postalCode: loc.postalCode || "",
                                  country: loc.country || "US",
                                });
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </Tip>
                          <Tip label="Delete Location" side="bottom">
                            <button
                              onClick={() =>
                                requestDelete(
                                  "Location",
                                  loc.id,
                                  loc.title,
                                  () => handleDeleteLocation(loc.id),
                                )
                              }
                              disabled={deletingLocationId === loc.id}
                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </Tip>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-5 text-center py-12">
                <MapPin className="size-8 text-muted-foreground mb-3 mx-auto" />
                <p className="text-sm font-medium">No locations defined</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Add offices, data centers, or cloud regions.
                </p>
              </div>
            )}
          </div>
          {filtLocations.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtLocations.length)}–{Math.min(tabPage * tabPageSize, filtLocations.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtLocations.length}
                </span>
              </p>
              {Math.ceil(filtLocations.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtLocations.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                            ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                            : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtLocations.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtLocations.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
